'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { BookOpen, ArrowRight } from 'lucide-react'

interface ResourceLink {
  href: string
  label: string
  description: string
}

interface AutomotiveResourcesSectionProps {
  resources: ResourceLink[]
}

export function AutomotiveResourcesSection({ resources }: AutomotiveResourcesSectionProps) {
  return (
    <section className="relative py-24 overflow-hidden bg-muted/20">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12 text-center mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2F5FE8]/10 border border-[#2F5FE8]/20 mb-6"
          >
            <BookOpen className="h-4 w-4 text-[#2F5FE8]" />
            <span className="eyebrow text-[#2F5FE8]">
              Keep Learning
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
          >
            Extend your automotive playbooks.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="reveal reveal-delay-2 text-lg text-muted-foreground"
          >
            Browse fresh guides, templates, and tools built for independent dealers and service teams.
          </motion.p>
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {resources.map((item, index) => (
            <motion.div
              key={item.href}
              initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.1,
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1]
              }}
              className={`reveal ${['reveal-delay-0', 'reveal-delay-1', 'reveal-delay-2', 'reveal-delay-3'][index % 4]}`}
            >
              <Link
                to={item.href}
                className="group lift-hover relative block h-full rounded-3xl overflow-hidden bg-white dark:bg-zinc-900 p-8 transition-all duration-300"
                style={{
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                }}
              >
                {/* Gradient Glow */}
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-2/3 opacity-0 group-hover:opacity-60 transition-opacity duration-300"
                  style={{
                    background: 'radial-gradient(ellipse at bottom, rgba(47, 95, 232, 0.15) -20%, transparent 70%)',
                    filter: 'blur(40px)',
                  }}
                />

                {/* Bottom Border Glow */}
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(90deg, rgba(47, 95, 232, 0.1) 0%, rgba(47, 95, 232, 0.5) 50%, rgba(47, 95, 232, 0.1) 100%)',
                    boxShadow: '0 0 15px 3px rgba(47, 95, 232, 0.2)',
                  }}
                />

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-lg font-bold text-foreground mb-3">
                    {item.label}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {item.description}
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#2F5FE8]">
                    Explore
                    <ArrowRight className="h-4 w-4 translate-x-0 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
